import React, {useEffect, useMemo, useRef} from 'react';
import './styles.css';
import PageTitle from '../../../../uiToolkit/PageTitle';
import {analyticsText} from '../../analyticsText';
import Dropdown from '../../../../uiToolkit/Inputs/Dropdown';
import SearchInput from '../../../../uiToolkit/Inputs/SearchInput';
import Button from '../../../../uiToolkit/Buttons/Button';
import {
  AvgGradeLearnerListModel,
  AvgLearnerGradeModel,
  CourseAvgGradesModel,
  CourseWithAvgGradeModel,
} from '../../../../data/models/Gradebook/LearnerAvgGradesModel';
import {LearnerStatistics} from '../../../../data/models/Workspace/Dashboard';
import OverviewGraph from '../../../../uiToolkit/Analytics/OverviewGraph';
import AnalyticsCard from '../../../../uiToolkit/Analytics/AnalyticsCard';
import AnalyticsEmptyState from '../../../../uiToolkit/Analytics/AnalyticsEmptyState';
import GradesTable, {
  GRADE_FAIL,
  GRADE_PROGRESS,
  GRADE_SUCCESS,
} from '../../components/GradesTable';
import {hasValue} from '../../../../utils/typecheck';
import TableLegend from '../../components/TableLegend';
import {
  AnalyticsGraphSkeleton,
  AnalyticsCardsSkeleton,
  AnalyticsDropdownSkeleton,
  AnalyticsTableSkeleton,
} from '../../components/AnalyticsSkeletons';
import {truncateNumberTo2Decimals} from '../../../../utils/getTimeTextFromSeconds';
import {Tour} from '../../../../modules/Tour';
import TablePagination from '../../../../uiToolkit/TablePagination';
import {LEARNERS_PER_PAGE} from '../conf';

interface Props {
  search: string;
  setSearch: (value: string) => void;
  learners: AvgGradeLearnerListModel[];
  courses: CourseWithAvgGradeModel[];
  grades: {[key: string]: AvgLearnerGradeModel[]};
  coursesAnalytics?: CourseAvgGradesModel;
  learnerStatistics?: LearnerStatistics;
  orderAsc: boolean;
  setOrderAsc: (value: boolean) => void;
  setSelectedCourse: (value: string) => void;
  selectedCourse: string;
  isLoading: boolean;
  // Per-widget preloaders: true until the first response arrives.
  coursesLoading: boolean;
  statisticsLoading: boolean;
  learnersLoading: boolean;
  page: number;
  pagesCount: number;
  onChangePage: (page: number) => void;
  onExport: () => void;
  exporting: boolean;
}

const AnalyticsUI = React.memo(
  ({
    search,
    setSearch,
    learners,
    courses,
    grades,
    coursesAnalytics,
    learnerStatistics,
    orderAsc,
    setOrderAsc,
    setSelectedCourse,
    selectedCourse,
    isLoading,
    coursesLoading,
    statisticsLoading,
    learnersLoading,
    page,
    pagesCount,
    onChangePage,
    onExport,
    exporting,
  }: Props) => {
    const tableRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      tableRef.current?.scrollTo({top: 0});
    }, [learners]);

    const mapCoursesForAvgGrades = () => {
      const result: any = {
        scores: {},
      };
      if (hasValue(coursesAnalytics?.globalAvgScore)) {
        result.overallAvgScore = truncateNumberTo2Decimals(
          coursesAnalytics!.globalAvgScore,
        );
      }
      courses.map(c => {
        if (hasValue(c.avgScore)) {
          result.scores[c.id] = {
            grade: c.avgScore,
            status:
              !c.isGradable || c.avgScore! >= c.passingScore
                ? GRADE_SUCCESS
                : GRADE_FAIL,
          };
        }
      });
      return result;
    };

    const mapLearnerRecords = () => {
      return learners.map(learner => ({
        id: learner.learnerId,
        name: learner.name,
        avatarUrl: learner.avatarUrl,
        scores: getLearnerGrades(learner.learnerId),
      }));
    };

    const getLearnerGrades = (learnerId: string) => {
      const scores: any = {};
      grades[learnerId]?.map(grade => {
        if (!grade.isGradable) {
          scores[grade.courseId] = {
            grade: grade.grade,
            status: GRADE_SUCCESS,
            hasStarted: grade.isStarted,
          };
        } else if (hasValue(grade.grade)) {
          scores[grade.courseId] = {
            grade: grade.grade,
            status:
              grade.grade! >= grade.passingScore ? GRADE_SUCCESS : GRADE_FAIL,
            hasStarted: grade.isStarted,
          };
        } else {
          scores[grade.courseId] = {
            status: GRADE_PROGRESS,
            hasStarted: grade.isStarted,
          };
        }
      });
      return scores;
    };

    const headerItems = useMemo(
      () => courses.map(c => ({title: c.name, id: c.id})),
      [courses],
    );
    const avgScores = useMemo(mapCoursesForAvgGrades, [
      courses,
      coursesAnalytics,
    ]);
    const learnerRecords = useMemo(mapLearnerRecords, [learners, grades]);

    return (
      <div className="AnalyticsContainer">
        <Tour section="analytics" />
        <div className="HeadingSection">
          <div className="GraphSection">
            <PageTitle title={analyticsText.title} />
            {coursesLoading ? (
              <AnalyticsGraphSkeleton />
            ) : (
              <OverviewGraph
                completed={coursesAnalytics?.completedCourses || 0}
                inProgress={coursesAnalytics?.inProgressCourses || 0}
                notStarted={coursesAnalytics?.notStartedCourses || 0}
                label={analyticsText.courses}
              />
            )}
          </div>
          <div className="CoursesAndCardsSection">
            {coursesLoading ? (
              <AnalyticsDropdownSkeleton />
            ) : (
              <Dropdown
                options={courses.map(course => ({
                  key: course.id,
                  value: course.name,
                }))}
                selectedOption={selectedCourse}
                setSelectedOption={setSelectedCourse}
                placeholder={analyticsText.selectCourse}
                enableSearch
              />
            )}
            {statisticsLoading ? (
              <AnalyticsCardsSkeleton />
            ) : (
              <div className="AnalyticsCardsContainer">
                <AnalyticsCard
                  value={
                    <p>
                      {learnerStatistics?.totalLearnersCompletedLesson || 0}
                    </p>
                  }
                  label={analyticsText.totalLessonsCompletedTitle}
                />
                <AnalyticsCard
                  value={<p>{learnerStatistics?.totalLikes || 0}</p>}
                  label={analyticsText.totalLikesTitle}
                />
                <AnalyticsCard
                  value={<p>{learnerStatistics?.totalComments || 0}</p>}
                  label={analyticsText.totalCommentsTitle}
                />
              </div>
            )}
          </div>
        </div>
        <div className="TableLegendAndSearch">
          <TableLegend />
          <div className="SearchExport">
            <SearchInput value={search} onChange={setSearch} />
            <Button
              onClick={onExport}
              isLoading={exporting}
              icon="icon-download_ic"
              title={analyticsText.export}
              className="ExportButton"
            />
          </div>
        </div>
        {learnersLoading || coursesLoading ? (
          <AnalyticsTableSkeleton />
        ) : learners.length > 0 && courses.length > 0 ? (
          <>
            <div
              ref={tableRef}
              className={`AnalyticsTableContainer ${
                isLoading ? 'AnalyticsTableLoading' : ''
              }`}>
              <GradesTable
                setOrderAsc={setOrderAsc}
                orderAsc={orderAsc}
                headerItems={headerItems}
                avgScores={avgScores}
                learnerRecords={learnerRecords}
              />
            </div>
            {pagesCount > 1 && (
              <TablePagination
                currentPage={page}
                rowCount={pagesCount * LEARNERS_PER_PAGE}
                rowsPerPage={LEARNERS_PER_PAGE}
                onChangePage={onChangePage}
              />
            )}
          </>
        ) : (
          <AnalyticsEmptyState />
        )}
      </div>
    );
  },
);

export default AnalyticsUI;
